import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronRight, Copy, ExternalLink, Flag, Loader2, Pencil, Undo2, Wrench } from 'lucide-react';
import { CitationSourceDrawer } from '../kb/CitationSourceDrawer';
import { Button } from '../ui/button';
import { Input, Textarea } from '../ui/input';
import type { KbChange } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { reasonLineOf, REVIEW_TYPE_META, reviewTypeOf, shortDate, titleOf, undoMeaning, whyLineOf, type ReviewItem } from './review-model';
import { SectionDiff } from './SectionDiff';
import { useChangeReview, type ChangeReview } from './useChangeReview';

/**
 * One flagged change, said as plainly as it can be: why it is here in one line, then the change
 * itself, then the decision. The queue only holds conflicts, edits the agent was unsure of, and
 * sensitive information, so the card leads with which of the three — everything else a change carries
 * (the agent's reasoning, its sources, its evidence) stays one click away on the page and in the
 * run, rather than in front of every decision.
 */

type Resolved = (message: string, changedProjectId: string) => void;

const STATUS_WORD: Record<KbChange['reviewStatus'], string> = {
  PENDING: 'Waiting for review',
  NOT_REQUIRED: 'Applied without review',
  ACCEPTED: 'Accepted',
  REJECTED: 'Undone',
  KEPT_BOTH: 'Kept both versions',
  SUPERSEDED: 'Replaced by a later fix',
};

const KIND_WORD: Record<KbChange['kind'], string> = {
  CREATE: 'New page',
  UPDATE: 'Edited',
  DELETE: 'Removed',
  MERGE: 'Merged',
  MOVE: 'Moved',
};

function ReasonPill({ change, override }: { change: KbChange; override?: 'fix' }) {
  const type = override ?? reviewTypeOf(change);
  if (!type) return null;
  const meta = REVIEW_TYPE_META[type];
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset flex-shrink-0', meta.pill)}>
      {meta.label}
    </span>
  );
}

/** Where the page sits — respecting `KB_REVIEW_NODE_PATH`, which hides slugs from people who find them noise. */
function NodePathLine({ change }: { change: KbChange }) {
  const { hasCapability } = useAuth();
  if (!hasCapability('KB_REVIEW_NODE_PATH')) return null;
  return (
    <span className="block text-[11px] font-mono text-ink-faint truncate mt-0.5">
      {change.kind === 'MOVE' && change.fromPath ? `${change.fromPath} → ${change.path}` : change.path}
    </span>
  );
}

/** The change itself, the decision, and the two ways to correct it — nothing else. */
function ChangeReviewBody({ review, onOpenNode }: { review: ChangeReview; onOpenNode: (path: string, projectId: string) => void }) {
  const { change, diff } = review;
  const [wrongOpen, setWrongOpen] = useState(false);
  const readyToSend = review.drafts.length > 0 || review.feedback.trim().length > 0;

  return (
    <>
      <div className="px-4 py-3 space-y-3">
        {review.fixInFlight && (
          <p className="text-xs text-ink-secondary flex items-center gap-1.5">
            <Wrench size={12} className="text-orange-500 flex-shrink-0" />
            <span className="flex-1">The agent is reworking this page from your feedback.</span>
            <Link to={`/activity?run=${review.fixRun?.runId ?? change.fixRunId}`} className="text-orange-500 hover:text-orange-700 flex-shrink-0">
              Watch
            </Link>
          </p>
        )}

        {review.editing ? (
          <div className="space-y-2">
            {review.editorLoading ? (
              <p className="text-[11px] text-ink-faint flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" />Loading the page…</p>
            ) : (
              <>
                <Input value={review.editTitle} onChange={(e) => review.setEditTitle(e.target.value)} placeholder="Title" />
                <Textarea value={review.editBody} onChange={(e) => review.setEditBody(e.target.value)} rows={16} className="font-mono text-[12.5px]" />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" disabled={review.busy !== null} onClick={() => review.setEditing(false)}>Cancel</Button>
                  <Button size="sm" variant="primary" disabled={review.busy !== null || !review.editBody.trim()} onClick={review.saveEdit}>
                    {review.busy === 'edit' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Save
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : change.kind === 'MOVE' ? (
          <p className="text-sm text-ink flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-ink-muted">{change.fromPath}</span>
            <ArrowRight size={13} className="text-ink-faint" />
            <span className="font-mono text-xs text-ink">{change.path}</span>
          </p>
        ) : diff ? (
          <SectionDiff review={review} />
        ) : (
          <p className="text-xs text-ink-faint">No text changed.</p>
        )}

        {review.error && (
          <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">{review.error}</p>
        )}

        {review.lineEditCount > 0 && (
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[11px] text-ink-muted mr-auto">
              {review.lineEditCount} {review.lineEditCount === 1 ? 'line' : 'lines'} edited
            </span>
            <Button size="sm" disabled={review.busy !== null} onClick={review.discardLineEdits}>Discard</Button>
            <Button size="sm" variant="primary" disabled={review.busy !== null} onClick={review.saveLineEdits}>
              {review.busy === 'lines' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Save my edits
            </Button>
          </div>
        )}

        {wrongOpen && review.canReview && (
          <div className="space-y-2">
            <Textarea
              value={review.feedback}
              onChange={(e) => review.setFeedback(e.target.value)}
              rows={3}
              placeholder="What's wrong? The agent fixes the page from your words."
            />
            <div className="flex items-center gap-2 justify-end">
              {review.drafts.length > 0 && (
                <span className="text-[11px] text-ink-faint mr-auto">
                  + {review.drafts.length} line {review.drafts.length === 1 ? 'comment' : 'comments'}
                </span>
              )}
              <Button size="sm" disabled={review.busy !== null} onClick={() => setWrongOpen(false)}>Cancel</Button>
              <Button size="sm"
                variant="primary"
                disabled={review.busy !== null || !readyToSend}
                onClick={async () => { if (await review.startFix()) setWrongOpen(false); }}
              >
                {review.busy === 'feedback' ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                Send to the agent
              </Button>
            </div>
          </div>
        )}
      </div>

      {review.isPending ? (
        !review.editing && (
          <div className="border-t border-border-subtle px-4 py-2.5 flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="primary" disabled={review.busy !== null || review.fixInFlight} onClick={() => review.act('accept')}>
              {review.busy === 'accept' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Keep
            </Button>
            <Button size="sm" disabled={review.busy !== null || review.fixInFlight} onClick={() => review.act('reject')} title={undoMeaning(change.kind)}>
              {review.busy === 'reject' ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
              Undo
            </Button>
            {review.canReview && change.kind !== 'MOVE' && (
              <Button size="sm" disabled={review.busy !== null} onClick={review.openEditor}>
                <Pencil size={12} />
                Edit
              </Button>
            )}
            {review.canKeepBoth && change.flagReason === 'CONFLICT' && (
              <Button size="sm" disabled={review.busy !== null || review.fixInFlight} onClick={() => review.act('keepBoth')} title="Put the page back and file the new version beside it">
                {review.busy === 'keepBoth' ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
                Keep both
              </Button>
            )}
            <span className="ml-auto flex items-center gap-3">
              {review.canReview && !wrongOpen && (
                <button
                  onClick={() => { setWrongOpen(true); review.setError(null); }}
                  className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink transition-colors"
                >
                  <Flag size={11} />Something's wrong
                </button>
              )}
              <button
                onClick={() => onOpenNode(change.path, change.projectId)}
                className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink transition-colors"
              >
                <ExternalLink size={11} />Open page
              </button>
            </span>
          </div>
        )
      ) : (
        <div className="px-4 py-2 border-t border-border-subtle text-[11px] text-ink-faint">
          {STATUS_WORD[change.reviewStatus]}
          {change.reviewedAt ? ` · ${shortDate(change.reviewedAt)}` : ''}
          {change.reviewNote ? ` — ${change.reviewNote}` : ''}
        </div>
      )}

      <CitationSourceDrawer citation={review.selectedCitation} projectId={review.projectId} onClose={() => review.setSelectedCitation(null)} />
    </>
  );
}

// ─── One change ──────────────────────────────────────────────────────────────

export function ReviewCard({
  change, projectId, onResolved, onOpenNode,
}: {
  change: KbChange;
  projectId: string;
  onResolved: Resolved;
  onOpenNode: (path: string, projectId: string) => void;
}) {
  // Closed by default, pending or not: the header already says what is wrong (the reason) and why
  // (the agent's own line), which is enough to triage a queue; the diff opens on demand.
  const [open, setOpen] = useState(false);
  const review = useChangeReview({ change, projectId, active: open, onResolved });
  const reason = reasonLineOf(change);
  const why = whyLineOf(change);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3 text-left hover:bg-surface-subtle transition-colors">
        {open ? <ChevronDown size={13} className="text-ink-faint mt-1 flex-shrink-0" /> : <ChevronRight size={13} className="text-ink-faint mt-1 flex-shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <ReasonPill change={change} />
            {/* The page is context; the reason line below is what the reviewer acts on, so it carries the weight. */}
            <span className="text-xs font-normal text-ink-faint leading-snug truncate">{titleOf(change)}</span>
            <span className="ml-auto text-[11px] text-ink-faint flex-shrink-0">
              {KIND_WORD[change.kind]} · {shortDate(change.createdAt)}
            </span>
          </span>
          {reason && <span className="block mt-1.5 text-[15px] font-semibold text-ink leading-snug">{reason}</span>}
          <NodePathLine change={change} />
        </span>
        {/* Its own row across the whole card, from the left edge, wrapping rather than truncating. */}
        {why && (
          <span
            className={`basis-full flex items-baseline gap-2 px-3 py-2 rounded-lg text-xs leading-relaxed ${REVIEW_TYPE_META[reviewTypeOf(change) ?? 'unsure'].why}`}
          >
            <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide opacity-70">Why</span>
            <span className="min-w-0">{why}</span>
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-border-subtle">
          <ChangeReviewBody review={review} onOpenNode={onOpenNode} />
        </div>
      )}
    </div>
  );
}

// ─── One fix, several changes ────────────────────────────────────────────────

function GroupMember({
  change, projectId, onResolved, onOpenNode,
}: {
  change: KbChange;
  projectId: string;
  onResolved: Resolved;
  onOpenNode: (path: string, projectId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const review = useChangeReview({ change, projectId, active: open, onResolved });
  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-subtle transition-colors">
        {open ? <ChevronDown size={13} className="text-ink-faint" /> : <ChevronRight size={13} className="text-ink-faint" />}
        <span className="min-w-0 flex-1 text-xs font-medium text-ink truncate">{titleOf(change)}</span>
        <span className="text-[11px] text-ink-faint">{KIND_WORD[change.kind]}</span>
      </button>
      {open && (
        <div className="border-t border-border-subtle">
          <ChangeReviewBody review={review} onOpenNode={onOpenNode} />
        </div>
      )}
    </div>
  );
}

/**
 * The past changes of one fix run, as one card. Fix runs no longer send their changes here for
 * confirmation; this is how the earlier ones read under the other filters.
 */
export function ReviewGroupCard({
  item, projectId, onResolved, onOpenNode,
}: {
  item: ReviewItem;
  projectId: string;
  projectNameFor: (projectId: string) => string | null;
  onResolved: Resolved;
  onOpenNode: (path: string, projectId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const first = item.changes[0]!;
  const finding = first.run?.sweepFinding;
  const headline = finding ? `Your fix for “${finding.title}”` : `Your fix · ${item.changes.length} pages`;

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-subtle transition-colors">
        {open ? <ChevronDown size={13} className="text-ink-faint flex-shrink-0" /> : <ChevronRight size={13} className="text-ink-faint flex-shrink-0" />}
        <ReasonPill change={first} override="fix" />
        <span className="text-sm font-semibold text-ink truncate flex-1">{headline}</span>
        <span className="text-[11px] text-ink-faint flex-shrink-0">{shortDate(first.run?.queuedAt ?? first.createdAt)}</span>
      </button>
      {open && (
        <div className="border-t border-border-subtle p-3 space-y-1.5">
          {item.changes.map((change) => (
            <GroupMember key={change.id} change={change} projectId={projectId} onResolved={onResolved} onOpenNode={onOpenNode} />
          ))}
        </div>
      )}
    </div>
  );
}
